// src/auth/ClientDashboard.js - Complete Stable Version (Mobile Optimized)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  User, MessageSquare, Settings, Star, Send, Image as ImageIcon, Save, Lock, MapPin, Phone as PhoneIcon,
  UserCircle, Edit2, Trash2, X, Check, Calendar, Award, TrendingUp, Activity, AlertCircle, CheckCircle, Mail,
} from 'lucide-react';
import { ModernSpinner } from '../components/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

export default function ClientDashboard() {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('feedback');
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 5,
    message: '',
    image: null,
  });
  const [submitted, setSubmitted] = useState(false);
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit/Delete states
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setSettingsForm({
        name: user.name || '',
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      if (user.email) {
        fetchMyFeedbacks();
      }
    }
  }, [user]);

  const fetchMyFeedbacks = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/api/feedback/my-feedbacks`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Unauthorized - please login again');
        }
        setMyFeedbacks([]);
        return;
      }

      const data = await response.json();
      const list = Array.isArray(data.feedbacks) ? data.feedbacks : [];
      setMyFeedbacks(list);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setMyFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const feedbackData = {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || '',
        rating: feedbackForm.rating,
        message: feedbackForm.message,
        image: null,
      };

      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(feedbackData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFeedbackForm({ rating: 5, message: '', image: null });
          fetchMyFeedbacks();
        }, 3000);
      } else {
        alert(data.error || 'Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // USER EDIT FEEDBACK
  const handleEditFeedback = async () => {
    if (!editingFeedback) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/feedback/my/${editingFeedback._id}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            rating: editingFeedback.rating,
            message: editingFeedback.message,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || 'Failed to update feedback');
        return;
      }

      alert('✅ Feedback updated successfully!');
      setMyFeedbacks(prev =>
        prev.map(f => (f.id === editingFeedback._id ? data.feedback : f))
      );
      setEditingFeedback(null);
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert('❌ Failed to update feedback');
    } finally {
      setLoading(false);
    }
  };

  // USER DELETE FEEDBACK
  const handleDeleteFeedback = async feedbackId => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/feedback/my/${feedbackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || 'Failed to delete feedback');
        return;
      }

      alert('✅ Feedback deleted successfully!');
      setMyFeedbacks(prev => prev.filter(f => f._id !== feedbackId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('❌ Failed to delete feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = e => {
    const { name, value } = e.target;
    setSettingsForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingsSubmit = async e => {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsMessage({ type: '', text: '' });

    try {
      if (
        settingsForm.newPassword &&
        settingsForm.newPassword !== settingsForm.confirmPassword
      ) {
        setSettingsMessage({
          type: 'error',
          text: 'New passwords do not match',
        });
        setSettingsSaving(false);
        return;
      }

      const updateData = {
        name: settingsForm.name,
        phone: settingsForm.phone,
        city: settingsForm.city,
        state: settingsForm.state,
        country: settingsForm.country,
      };

      if (settingsForm.currentPassword && settingsForm.newPassword) {
        updateData.currentPassword = settingsForm.currentPassword;
        updateData.newPassword = settingsForm.newPassword;
      }

      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (response.ok) {
        setSettingsMessage({
          type: 'success',
          text: 'Profile updated successfully!',
        });
        updateUser(data.user);
        setSettingsForm(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      } else {
        setSettingsMessage({
          type: 'error',
          text: data.error || 'Failed to update profile',
        });
      }
    } catch (error) {
      console.error('Settings update error:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Network error. Please try again.',
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const renderFeedbackTab = () => (
    <div className="space-y-6 sm:space-y-8">
      {/* Submit Feedback Form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          Submit Your Feedback
        </h3>

        {submitted ? (
          <div className="text-center py-8 sm:py-12 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-2xl border-2 border-green-300 dark:border-green-700">
            <CheckCircle className="w-16 h-16 sm:w-20 sm:h-20 text-green-600 mx-auto mb-4 animate-bounce" />
            <h4 className="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-300 mb-2">
              Thank You!
            </h4>
            <p className="text-green-700 dark:text-green-400 text-sm sm:text-lg">
              Your feedback has been published successfully!
            </p>
          </div>
        ) : (
          <form onSubmit={handleFeedbackSubmit} className="space-y-5 sm:space-y-6">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Your Rating
              </label>
              <div className="flex gap-2 sm:gap-3">
                {[1, 2, 3, 4, 5].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() =>
                      setFeedbackForm(prev => ({ ...prev, rating: num }))
                    }
                    className={`transform transition-all hover:scale-125 ${
                      feedbackForm.rating >= num
                        ? 'text-yellow-400 drop-shadow-lg'
                        : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'
                    }`}
                  >
                    <Star
                      className="w-8 h-8 sm:w-10 sm:h-10"
                      fill={feedbackForm.rating >= num ? 'currentColor' : 'none'}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Your Feedback
              </label>
              <textarea
                value={feedbackForm.message}
                onChange={e =>
                  setFeedbackForm(prev => ({ ...prev, message: e.target.value }))
                }
                rows="4"
                required
                minLength="10"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white resize-none text-xs sm:text-base"
                placeholder="Share your experience (minimum 10 characters)..."
              />
            </div>

            <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800 text-xs sm:text-sm">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-blue-800 dark:text-blue-300">
                Your feedback will be published instantly on the homepage.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center justify-center gap-2 sm:gap-3 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-lg"
            >
              {loading ? (
                <>
                  <ModernSpinner size="sm" color="white" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  Publish Feedback
                </>
              )}
            </button>
          </form>
        )}
      </div>

      {/* My Feedback History */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
          <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
          My Feedback History ({myFeedbacks.length})
        </h3>

        {loading && myFeedbacks.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <ModernSpinner size="lg" />
            <p className="text-gray-600 dark:text-gray-400 mt-4 text-sm sm:text-base">
              Loading your feedbacks...
            </p>
          </div>
        ) : myFeedbacks.length === 0 ? (
          <div className="text-center py-8 sm:py-12 bg-gray-50 dark:bg-gray-700 rounded-xl">
            <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
              You haven't submitted any feedback yet
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {myFeedbacks.map(feedback => (
              <div
                key={feedback.id}
                className="p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-400 dark:hover:border-blue-600 transition-all bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                  <div className="flex items-center gap-1 sm:gap-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          i < feedback.rating
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 flex items-center gap-1 flex-shrink-0">
                    <CheckCircle className="w-3 h-3" />
                    Published
                  </span>
                </div>

                <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed text-xs sm:text-base">
                  {feedback.message}
                </p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs sm:text-sm text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-200 dark:border-gray-600 gap-2 sm:gap-0">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    {new Date(feedback.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>

                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => setEditingFeedback(feedback)}
                      className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 flex items-center justify-center gap-1 transition-colors text-xs sm:text-sm"
                    >
                      <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(feedback)}
                      className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 flex items-center justify-center gap-1 transition-colors text-xs sm:text-sm"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
          <UserCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        My Profile
      </h3>
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
          <div className="group">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <User className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              Name
            </label>
            <input
              type="text"
              defaultValue={user?.name}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl dark:bg-gray-700 dark:text-white bg-gray-50 cursor-not-allowed text-xs sm:text-base"
              readOnly
            />
          </div>
          <div className="group">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
              Email
            </label>
            <input
              type="email"
              defaultValue={user?.email}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl dark:bg-gray-700 dark:text-white bg-gray-50 cursor-not-allowed text-xs sm:text-base"
              readOnly
            />
          </div>
          <div className="group">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
              Phone
            </label>
            <input
              type="tel"
              defaultValue={user?.phone || 'Not provided'}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl dark:bg-gray-700 dark:text-white bg-gray-50 cursor-not-allowed text-xs sm:text-base"
              readOnly
            />
          </div>
          <div className="group">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
              Location
            </label>
            <input
              type="text"
              defaultValue={`${user?.city || ''}, ${user?.state || ''}, ${
                user?.country || ''
              }`}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl dark:bg-gray-700 dark:text-white bg-gray-50 cursor-not-allowed text-xs sm:text-base"
              readOnly
            />
          </div>
        </div>

        <div className="mt-6 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border-2 border-blue-200 dark:border-blue-800 rounded-xl text-xs sm:text-sm">
          <p className="text-blue-800 dark:text-blue-300 flex items-center gap-2">
            <Award className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            To update your profile information, please go to the{' '}
            <button
              onClick={() => setActiveTab('settings')}
              className="underline font-bold hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Settings
            </button>{' '}
            tab.
          </p>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
          <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        Account Settings
      </h3>

      <form onSubmit={handleSettingsSubmit} className="space-y-6 sm:space-y-8">
        <div className="p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <UserCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            Personal Information
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="group">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={settingsForm.name}
                onChange={handleSettingsChange}
                required
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                placeholder="John Doe"
              />
            </div>

            <div className="group">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                Phone Number
              </label>
              <div className="relative">
                <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                <input
                  type="tel"
                  name="phone"
                  value={settingsForm.phone}
                  onChange={handleSettingsChange}
                  className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
          </div>

          <div className="mt-3 sm:mt-4">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Email Address (Cannot be changed)
            </label>
            <input
              type="email"
              value={user?.email}
              disabled
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed text-xs sm:text-base"
            />
          </div>
        </div>

        <div className="p-4 sm:p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl border border-purple-200 dark:border-purple-800">
          <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            Location
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="group">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={settingsForm.city}
                onChange={handleSettingsChange}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                placeholder="Mumbai"
              />
            </div>

            <div className="group">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                State
              </label>
              <input
                type="text"
                name="state"
                value={settingsForm.state}
                onChange={handleSettingsChange}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                placeholder="Maharashtra"
              />
            </div>

            <div className="group">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={settingsForm.country}
                onChange={handleSettingsChange}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                placeholder="India"
              />
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl border border-red-200 dark:border-red-800">
          <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            Change Password (Optional)
          </h4>

          <div className="space-y-3 sm:space-y-4">
            <div className="group">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={settingsForm.currentPassword}
                onChange={handleSettingsChange}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                placeholder="Enter current password"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="group">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={settingsForm.newPassword}
                  onChange={handleSettingsChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                  placeholder="Enter new password"
                />
              </div>

              <div className="group">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={settingsForm.confirmPassword}
                  onChange={handleSettingsChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition-all hover:border-blue-400 text-xs sm:text-base"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
          </div>
        </div>

        {settingsMessage.text && (
          <div
            className={`p-3 sm:p-4 rounded-xl border-2 flex items-start gap-2 sm:gap-3 text-xs sm:text-base ${
              settingsMessage.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-800 dark:text-red-300'
            }`}
          >
            {settingsMessage.type === 'success' ? (
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
            )}
            <p className="font-semibold">{settingsMessage.text}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={settingsSaving}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:from-purple-700 hover:to-indigo-700 flex items-center justify-center gap-2 sm:gap-3 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-lg"
        >
          {settingsSaving ? (
            <>
              <ModernSpinner size="sm" color="white" />
              Saving Changes...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 sm:w-5 sm:h-5" />
              Save Changes
            </>
          )}
        </button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20 py-6 sm:py-12 px-2 sm:px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8 text-center">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-base">
            Manage your account and feedback
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 bg-white dark:bg-gray-800 p-1 sm:p-2 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('feedback')}
            className={`flex-1 min-w-[100px] sm:min-w-[150px] px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-base ${
              activeTab === 'feedback'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Feedback
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 min-w-[100px] sm:min-w-[150px] px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-base ${
              activeTab === 'profile'
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <User className="w-4 h-4" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 min-w-[100px] sm:min-w-[150px] px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-base ${
              activeTab === 'settings'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>

        {/* Content */}
        {activeTab === 'feedback' && renderFeedbackTab()}
        {activeTab === 'profile' && renderProfile()}
        {activeTab === 'settings' && renderSettings()}
      </div>

      {/* Edit Modal */}
      {editingFeedback && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full p-4 sm:p-8 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-2">
              <Edit2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              Edit Feedback
            </h3>

            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  Rating
                </label>
                <div className="flex gap-2 sm:gap-3">
                  {[1, 2, 3, 4, 5].map(num => (
                    <button
                      key={num}
                      type="button"
                      onClick={() =>
                        setEditingFeedback(prev => ({ ...prev, rating: num }))
                      }
                      className={`transform transition-all hover:scale-125 ${
                        editingFeedback.rating >= num
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    >
                      <Star
                        className="w-8 h-8 sm:w-10 sm:h-10"
                        fill={
                          editingFeedback.rating >= num ? 'currentColor' : 'none'
                        }
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  value={editingFeedback.message}
                  onChange={e =>
                    setEditingFeedback(prev => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  rows="4"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white resize-none text-xs sm:text-base"
                />
              </div>
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button
                onClick={handleEditFeedback}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50 text-xs sm:text-base"
              >
                {loading ? (
                  <>
                    <ModernSpinner size="sm" color="white" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={() => setEditingFeedback(null)}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-xs sm:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-4 sm:p-8 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Delete Feedback?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-base">
                Are you sure you want to delete this feedback? This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => handleDeleteFeedback(deleteConfirm._id)}
                disabled={loading}
                className="flex-1 bg-red-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50 text-xs sm:text-base"
              >
                {loading ? (
                  <>
                    <ModernSpinner size="sm" color="white" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Yes, Delete
                  </>
                )}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-xs sm:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}